import { useState, useCallback } from 'react'

/**
 * useConnectOnDrop — Detects when the user releases a handle on the empty canvas
 * and manages the state of the node creation popup.
 *
 * Uses the onConnectEnd event from React Flow:
 * - connectionState.isValid = false → released on empty canvas (not on another node)
 * - connectionState.fromHandle.id → which handle originated the drag
 *
 * Ignored handles: 'fallback' (must be connected manually)
 */

interface DropState {
  screenX: number;
  screenY: number;
  flowX: number;
  flowY: number;
  fromNodeId: string;
  fromHandle: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useConnectOnDrop({ rfInstanceRef }: { rfInstanceRef: React.MutableRefObject<any> }) {
  const [dropState, setDropState] = useState<DropState | null>(null)
  // dropState: { screenX, screenY, flowX, flowY, fromNodeId, fromHandle } | null

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onConnectEnd = useCallback((event: any, connectionState: any) => {
    // Only acts when released on the empty canvas (no valid target)
    if (connectionState?.isValid) return

    const fromHandle = connectionState?.fromHandle?.id ?? ''
    const fromNodeId = connectionState?.fromNode?.id ?? null

    // Ignora handle de fallback — deve ser conectado manualmente
    if (fromHandle === 'fallback') return
    // Ignore if there is no source node
    if (!fromNodeId) return

    // Screen coordinates where the user released
    const screenX = event?.clientX ?? 0
    const screenY = event?.clientY ?? 0

    // Convert to flow canvas coordinates
    const flowPos = rfInstanceRef.current
      ? rfInstanceRef.current.screenToFlowPosition({ x: screenX, y: screenY })
      : { x: screenX, y: screenY }

    setDropState({
      screenX,
      screenY,
      flowX: flowPos.x,
      flowY: flowPos.y,
      fromNodeId,
      fromHandle,
    })
  }, [rfInstanceRef])

  const dismiss = useCallback(() => setDropState(null), [])

  return { onConnectEnd, dropState, dismiss }
}
